import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useServiceCalculatorApi } from '@hooks/useServiceCalculatorApi';
import { ServiceCalculatorOfferResponse } from '@appTypes/ServiceCalculatorTypes';
import {
  FeedbackForm,
  ServiceCalculatorContextProps,
  VehicleForm,
  WorkProposalForm,
} from './ServiceCalculatorContext.types';

const EMPTY_OFFER: ServiceCalculatorOfferResponse = {
  isCompleted: false,
  isPossible: false,
  vehicleMaintenanceWizard: undefined,
};

export const useServiceCalculatorLogic = (): ServiceCalculatorContextProps => {
  const { getOfferFn, getOfferWithPossibleEquipmentFn, getOfferWithSelectedWorkProposalFn, sendFeedbackFormFn } =
    useServiceCalculatorApi();
  const [offer, setOffer] = useState<ServiceCalculatorOfferResponse>(EMPTY_OFFER);
  const [shouldShowAnOffer, setShouldShowAnOffer] = useState(false);
  const [shouldShowManualRequest, setShouldShowManualRequest] = useState(false);
  const [shouldShowFeedbackConfirmation, setShouldShowFeedbackConfirmation] = useState(false);
  const vehicleForm = useForm<VehicleForm>({
    mode: 'onChange',
    defaultValues: { licensePlateNumber: '', vehicleName: '', isEv: false, equipments: [] },
  });
  const workProposalForm = useForm<WorkProposalForm>({
    mode: 'onChange',
    defaultValues: { workProposal: -1 },
  });
  const feedbackForm = useForm<FeedbackForm>({ mode: 'onChange', defaultValues: { comments: '' } });

  const showManualOffer = useCallback(() => {
    setShouldShowManualRequest(true);
    setShouldShowAnOffer(false);
  }, []);

  const applyOffer = useCallback((data: ServiceCalculatorOfferResponse) => {
    setOffer(data);
    setShouldShowAnOffer(data.isCompleted && data.isPossible && !!data.vehicleMaintenanceWizard.offerRequest);
    setShouldShowManualRequest(
      data.isCompleted && (!data.isPossible || (data.isPossible && !data.vehicleMaintenanceWizard.offerRequest)),
    );
  }, []);

  const getOffer = useCallback(
    async (form: VehicleForm) => {
      try {
        const data = await getOfferFn({
          licensePlateNumber: form.licensePlateNumber,
          centerId: form.centerId,
          mileage: form.mileage,
        });
        if (!data) return showManualOffer();
        vehicleForm.setValue('equipments', data.vehicleMaintenanceWizard.possibleEquipment ?? []);
        applyOffer(data);
      } catch {
        showManualOffer();
      }
      return undefined;
    },
    [applyOffer, getOfferFn, showManualOffer, vehicleForm],
  );

  const getOfferWithPossibleEquipment = useCallback(
    async (form: VehicleForm) => {
      setOffer((current) => ({
        ...current,
        vehicleMaintenanceWizard: { ...current.vehicleMaintenanceWizard, workProposalGroups: [] },
      }));
      const offerRequest = {
        ...offer.vehicleMaintenanceWizard,
        possibleEquipment: form.equipments,
        nextAction: { ...offer.vehicleMaintenanceWizard.nextAction, name: 'AddWorkProposalGroups' },
        workProposalGroups: [],
      };
      try {
        const data = await getOfferWithPossibleEquipmentFn(offerRequest);
        if (!data) return showManualOffer();
        applyOffer(data);
      } catch {
        showManualOffer();
      }
      return undefined;
    },
    [applyOffer, getOfferWithPossibleEquipmentFn, offer, showManualOffer],
  );

  const getOfferWithSelectedWorkProposal = useCallback(
    async (form: WorkProposalForm) => {
      const offerRequest = {
        ...offer.vehicleMaintenanceWizard,
        workProposalGroups: offer.vehicleMaintenanceWizard.workProposalGroups.map((proposal) => ({
          ...proposal,
          isSelected: proposal.value === form.workProposal,
        })),
      };
      try {
        const data = await getOfferWithSelectedWorkProposalFn(offerRequest);
        if (!data) return showManualOffer();
        applyOffer(data);
      } catch {
        showManualOffer();
      }
      return undefined;
    },
    [applyOffer, getOfferWithSelectedWorkProposalFn, offer, showManualOffer],
  );

  const sendFeedbackForm = useCallback(
    async (email: string, comments: string, vehicleText: string, documentId: string) => {
      try {
        const isSent = await sendFeedbackFormFn(email, comments, vehicleText, documentId);
        setShouldShowFeedbackConfirmation(isSent);
        return isSent;
      } catch {
        setShouldShowFeedbackConfirmation(false);
        return false;
      }
    },
    [sendFeedbackFormFn],
  );

  const resetState = useCallback(() => {
    vehicleForm.reset();
    workProposalForm.reset();
    feedbackForm.reset();
    setOffer(EMPTY_OFFER);
    setShouldShowAnOffer(false);
    setShouldShowManualRequest(false);
  }, [vehicleForm, workProposalForm, feedbackForm]);

  const resetOffer = useCallback(() => {
    vehicleForm.setValue('equipments', []);
    vehicleForm.setValue('isEv', false);
    vehicleForm.setValue('vehicleName', '');
    setOffer(EMPTY_OFFER);
  }, [vehicleForm]);

  return useMemo(
    () => ({
      offer,
      getOffer,
      getOfferWithPossibleEquipment,
      getOfferWithSelectedWorkProposal,
      resetState,
      resetOffer,
      shouldShowAnOffer,
      shouldShowManualRequest,
      updateShouldShowAnOffer: setShouldShowAnOffer,
      updateShouldShowManualRequest: setShouldShowManualRequest,
      vehicleForm,
      workProposalForm,
      feedbackForm,
      sendFeedbackForm,
      shouldShowFeedbackConfirmation,
    }),
    [
      offer,
      getOffer,
      getOfferWithPossibleEquipment,
      getOfferWithSelectedWorkProposal,
      resetState,
      resetOffer,
      shouldShowAnOffer,
      shouldShowManualRequest,
      vehicleForm,
      workProposalForm,
      feedbackForm,
      sendFeedbackForm,
      shouldShowFeedbackConfirmation,
    ],
  );
};
